import React, {useState, useEffect, useRef} from 'react';
import {Row, Col, Skeleton, Modal, Calendar, Button, Form, Input, message, Spin, Space, Divider} from 'antd';
import 'antd/dist/antd.css'; // or 'antd/dist/antd.less'
import localeMoment from 'moment';
import 'moment/min/locales'; // solution from https://github.com/moment/moment/issues/2979#issuecomment-405657654
import moment from 'moment-timezone';
import _ from 'underscore';
import {
    CalendarOutlined,
    DoubleLeftOutlined,
    DoubleRightOutlined,
    GlobalOutlined,
    LeftOutlined
} from "@ant-design/icons";
import PerfectScrollbar from 'react-perfect-scrollbar';
import ExceedAvatarComponent from './ExceedAvatarComponent';
import 'react-perfect-scrollbar/dist/css/styles.css';
import './MeetingSchedulerPage.scss';

import queryString from 'query-string';
import {callApi} from '../Utills/ApiRest';
import {useRouteMatch, useLocation} from "react-router-dom";
import {testEmailRegex, testPhoneRegex} from '../Utills/regex';
import {ClientMessages, getMessage} from '../Utills/locale';
import * as Sentry from "@sentry/react";

function MeetingSchedulerPage() {
    let match = useRouteMatch();
    let location = useLocation();
    let scrollRef = useRef(null);
    let pulledMonths = useRef([]);
    let availableMonths = useRef([]);

    const [loading, setLoading] = useState(true);
    const [loadingMonth, setLoadingMonth] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showSlots, setShowSlotsValue] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [token, setToken] = useState(match.params.token);
    const [scheduledMeeting, setScheduledMeeting] = useState();
    const [selectedDate, setSelectedDateValue] = useState();
    const [selectedSlot, setSelectedSlot] = useState({});
    const [meetingSlots, setMeetingSlots] = useState({});
    const [meetingLength, setMeetingLength] = useState(1800000);

    const [repName, setRepName] = useState("");
    const [repCompany, setRepCompany] = useState("");
    const [repImgUrl, setRepImgUrl] = useState("");
    const [botImgUrl, setBotImgUrl] = useState("")
    const [timezone, setTimezone] = useState();
    const [locale, setLocale] = useState();
    const [firstAvailableDay, setFirstAvailableDay] = useState("");
    const [lastAvailableDay, setLastAvailableDay] = useState("");
    const [collectPhoneNumber, setCollectPhoneNumber] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState();
    const [phoneNumberError, setPhoneNumberError] = useState("");
    const [contactName, setContactName] = useState();
    const [contactNameError, setContactNameError] = useState("");
    const [collectEmail, setCollectEmail] = useState(false);
    const [contactEmail, setContactEmail] = useState();
    const [contactEmailError, setContactEmailError] = useState("")
    const [isFromPermanentLink, setIsFromPermanentLink] = useState(false);
    const [showCopyrights, setShowCopyrights] = useState(true);

    // eslint-disable-next-line
    useEffect(() => {
        if (!(_.isEmpty(phoneNumber) || !testPhoneRegex(phoneNumber))) {
            setPhoneNumberError("");
        }

        if (!(_.isEmpty(contactName))) {
            setContactNameError("");
        }

        if (!(_.isEmpty(contactEmail) || !testEmailRegex(contactEmail))) {
            setContactEmailError("");
        }
    }, [phoneNumber, contactName, contactEmail]);


    // eslint-disable-next-line
    useEffect(() => {
        if (Object.keys(meetingSlots).length === 0) {
            return;
        }

        let suggestedSlotStart = queryString.parse(location.search)?.suggestedSlotStart;

        if (suggestedSlotStart === '0') {
            message.error('Meeting could not be set, please choose an alternative time');
        } else if (suggestedSlotStart && !isNaN(suggestedSlotStart)) {
            suggestedSlotStart = Number(suggestedSlotStart);
            let suggestedSlot = moment.tz(suggestedSlotStart, timezone);

            if (!pulledMonths.current.includes(suggestedSlot.format(MONTH_FORMAT))) {
                getAvailableSlotsForMonth(suggestedSlot, suggestedSlotStart);
            } else if (meetingSlots.hasOwnProperty(suggestedSlot.format(DATE_FORMAT))) {
                setSelectedDate(suggestedSlot);

                // eslint-disable-next-line
                let selectedSlot = getCurrentSlots(suggestedSlot).find(slot => slot.startTime.value === suggestedSlotStart);

                if (selectedSlot) {
                    onSlotClick(selectedSlot);
                }
            }
        }
        // eslint-disable-next-line
    }, [meetingSlots])

    useEffect(() => {
        getAvailableSlots();
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (timezone && locale) {
            if (locale !== "en") {
                localeMoment.locale([locale, "en-gb"]);
            } else {
                let selectedZoneData = moment.tz.zone(timezone);

                if (selectedZoneData) {
                    let countries = selectedZoneData.countries() || [];

                    if (countries.length > 0 && !countries.includes("US")) {
                        let locales = countries.map(country => `en-${country.toLowerCase()}`);
                        locales.push("en-gb"); // fallback - for countries that don't have specific locales for english (for example Korea) we use the British locale without am/pm
                        localeMoment.locale(locales);
                    }
                }
            }

            // Based on the solution here - https://github.com/moment/moment-timezone/issues/647#issuecomment-439600573
            // moment-timezone doesn't have locale data, moment with locales doesn't have moment.tz(...)
            let localeCode = localeMoment.locale();
            let localeConfig = localeMoment.localeData()._config;

            // Default is AM/PM, but we want am/pm
            if (localeConfig?.longDateFormat?.LT) {
                localeConfig.longDateFormat.LT = localeConfig.longDateFormat.LT.replace("A", "a");
            }

            if (localeConfig?.weekdaysMin) {
                localeConfig.weekdaysMin = (localeConfig.weekdaysShort?.length > 0 ? localeConfig.weekdaysShort.slice() : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]);
            }

            moment.defineLocale(localeCode, localeConfig);
            moment.locale(localeCode);
        }
    }, [timezone, locale])

    useEffect(() => {
        if (selectedDate) {
            scrollToWorktimeSlots();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate]);

    const setSelectedDate = (selectedDate) => {
        setSelectedDateValue(selectedDate);
        setShowSlots(true);
    }

    const setShowSlots = (showSlots) => {
        if (!showSlots && scrollRef) {
            scrollRef.current._container.scrollTop = 0;
        }

        setShowSlotsValue(showSlots)
    }

    const getAvailableSlots = async () => {
        let extraParams = queryString.parse(location.search);
        let mode = 'otherSuggestionsFull';

        if (extraParams?.mode) {
            mode = extraParams.mode;
            setIsFromPermanentLink(true);
        }

        let suggestedTimezone = "";

        try {
            suggestedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (e) {
        }

        let meetingsDTO = await getRepAvailableSlots(token, mode, suggestedTimezone);
        setMeetingDTO(meetingsDTO);
        setLoading(false);
    }

    const getAvailableSlotsForMonth = async (newMonth, suggestedSlotStart) => {
        setLoadingMonth(true);
        const monthString = newMonth.format(MONTH_FORMAT);
        const {
            meetings,
            firstAvailableDay: firstAvailableDayString
        } = await getRepAvailableSlotsForMonth(token, monthString, timezone);
        pulledMonths.current.push(monthString);

        if (meetings) {
            setMeetingSlots(Object.assign(meetingSlots, meetings));

            if (firstAvailableDayString?.length > 0) {
                let firstAvailableDayMoment = moment(firstAvailableDayString, DATE_FORMAT);
                setSelectedDateValue(firstAvailableDayMoment.clone());
                availableMonths.current.push(firstAvailableDayMoment.format(MONTH_FORMAT));

                while (firstAvailableDayMoment.isAfter(newMonth, "month")) {
                    pulledMonths.current.push(newMonth.add(1, "months").format(MONTH_FORMAT));
                }
            }

            if (suggestedSlotStart && meetings.hasOwnProperty(newMonth.format(DATE_FORMAT))) {
                setSelectedDate(newMonth);

                // eslint-disable-next-line
                let selectedSlot = getCurrentSlots(newMonth).find(slot => slot.startTime.value === suggestedSlotStart);

                if (selectedSlot) {
                    onSlotClick(selectedSlot);
                }
            }
        }

        setLoadingMonth(false)
    }

    //region Api Calls functions
    const getRepAvailableSlots = (token, mode, userTimeZone) => {
        return callApi(`/external/callback?token=${token}&mode=${mode}&userTimeZone=${userTimeZone}`, {}, {method: 'get'});
    }

    const setCustomMeeting = (token, meetingTime, timezone, contactPhoneNumber, contactName, contactEmail) => {
        return callApi(`/external/callback?token=${token}&mode=setCustom&meetingTime=${meetingTime}&timezone=${timezone}${collectPhoneNumber ? `&contactPhoneNumber=${contactPhoneNumber}` : ""}&isFromPermanentLink=${isFromPermanentLink}&contactName=${contactName}${(collectEmail || isFromPermanentLink) ? `&contactEmail=${encodeURIComponent(contactEmail)}` : ""}`, {}, {method: 'get'});
    }

    const getRepAvailableSlotsForMonth = (token, month, userTimeZone) => {
        return callApi(`/external/callback?token=${token}&mode=otherSuggestionsMonth&monthString=${month}&userTimeZone=${userTimeZone}`, {}, {method: 'get'});
    }
    //endregion

    const setMeetingDTO = (meetingsDTO) => {
        setMeetingLength(meetingsDTO.meetingLength);
        setRepName(meetingsDTO.repName);
        setRepCompany(meetingsDTO.repProject);
        setRepImgUrl(meetingsDTO.repImgUrl);
        setBotImgUrl(meetingsDTO.botImgUrl);
        setCollectPhoneNumber(meetingsDTO.collectPhoneNumber && _.isEmpty(meetingsDTO.existingMeeting));
        setCollectEmail(meetingsDTO.collectEmail && _.isEmpty(meetingsDTO.existingMeeting));
        setScheduledMeeting(meetingsDTO.existingMeeting);
        setLocale(meetingsDTO.languageCode === "es" ? "es" : "en");
        setShowCopyrights(meetingsDTO.showCopyrights ?? true);

        if (meetingsDTO.rescheduleToken?.length > 0 && meetingsDTO.rescheduleToken !== token) {
            setToken(meetingsDTO.rescheduleToken);
        }

        if (meetingsDTO.userTimezone) {
            setTimezone(meetingsDTO.userTimezone);
        } else {
            try {
                setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
            } catch (e) {
            }
        }

        let firstAvailableDayMoment = moment(meetingsDTO.firstAvailableDay, DATE_FORMAT);
        setFirstAvailableDay(firstAvailableDayMoment.clone());
        setSelectedDateValue(firstAvailableDayMoment.clone());

        let lastAvailableDayMoment = moment(meetingsDTO.lastAvailableDay, DATE_FORMAT);
        let nowMoment = moment();
        pulledMonths.current.push(nowMoment.format(MONTH_FORMAT));
        availableMonths.current.push(firstAvailableDayMoment.format(MONTH_FORMAT));
        availableMonths.current.push(lastAvailableDayMoment.format(MONTH_FORMAT));

        while (lastAvailableDayMoment.isAfter(nowMoment, "month")) {
            pulledMonths.current.push(nowMoment.add(1, "months").format(MONTH_FORMAT));
        }

        setLastAvailableDay(moment().add(90, "d"));

        if (meetingsDTO.meetings) {
            setMeetingSlots(meetingsDTO.meetings);
        } else {
            setMeetingSlots({});
        }
    }

    const scrollToWorktimeSlots = () => {
        let currSlots = getCurrentSlots();

        if (currSlots.length > 4) {
            let index = currSlots.findIndex(slot => {
                let slotMoment = moment.tz(slot.startTime.value, timezone || slot.timeZoneName);
                return slotMoment && slotMoment.hour() >= WORK_DAY_START_HOUR && slotMoment.hour() <= WORK_DAY_END_HOUR;
            });

            if (index >= 0) {
                setTimeout(() => {
                    if (scrollRef) {
                        scrollRef.current._container.scrollTop = index * TIME_SLOT_HEIGHT;
                    }
                }, 200);
            }
        }
    }

    const showingCalendar = () => (!showSlots && !showConfirm);
    const showingSlots = () => (showSlots && !showConfirm);
    const checkDate = (date) => !meetingSlots.hasOwnProperty(date.format(DATE_FORMAT));

    const DATE_FORMAT = "YYYY-MM-DD";
    const MONTH_FORMAT = "YYYY-MM";
    const WORK_DAY_START_HOUR = 8;
    const WORK_DAY_END_HOUR = 17;
    const TIME_SLOT_HEIGHT = 62;


    const onSlotClick = (timeSlot) => {
        setSelectedSlot(timeSlot);
        setShowConfirm(true);
    }

    const getTimezoneString = () => {
        let timezoneFormatted = moment().tz(timezone).format("Z");
        return `UTC${timezoneFormatted} (${timezone})`;
    };

    // eslint-disable-next-line
    const getSelectedDateString = (suggestedSlot) => (suggestedSlot ? suggestedSlot.format(DATE_FORMAT) : selectedDate?.format(DATE_FORMAT));
    // eslint-disable-next-line
    const getCurrentSlots = (suggestedSlot) => {
        let selectedDateString = suggestedSlot ? getSelectedDateString(suggestedSlot) : getSelectedDateString();
        let slots = meetingSlots[selectedDateString];

        if (slots) {
            return slots
        } else {
            return [];
        }
    };

    const getSelectedEndTimeMoment = () => moment.tz(selectedSlot.endTime?.value, timezone);
    const getSelectedTimeString = () => `${getSelectedTimeMoment().format("dddd, MMM Do, LT")} - ${getSelectedEndTimeMoment().format("LT")}`;
    const getSelectedTimeMoment = () => moment.tz(getSelectedTime(), timezone);
    const getSelectedTime = () => selectedSlot.startTime?.value;
    const getSelectedTimezoneString = () => `UTC${getSelectedTimeMoment().format("Z")} (${timezone})`;
    const getScheduledTimeString = () => moment.tz(scheduledMeeting?.startTime.value, timezone).format("dddd, MMM Do, LT");

    const changeMonth = (factor) => {
        let newMonth = selectedDate.clone().add(factor, "month");

        if (pulledMonths.current.includes(newMonth.format(MONTH_FORMAT))) {
            while (!availableMonths.current.includes(newMonth.format(MONTH_FORMAT))) {
                newMonth.add(factor, "month");
            }

            let firstOfMonth = Object.keys(meetingSlots).map(day => moment(day, DATE_FORMAT))
                .filter(dayMoment => dayMoment.isSame(newMonth, "month")).sort((a, b) => a.isBefore(b) ? -1 : 1)?.[0];

            if (firstOfMonth) {
                setSelectedDateValue(firstOfMonth);
            }
        } else {
            getAvailableSlotsForMonth(newMonth);
        }
    }

    const calendarHeader = ({value}) => {
        return (
            <Row type="flex" justify="space-between" align="middle" className="p-5-1">
                <Col>
                    <DoubleLeftOutlined
                        className={`ml-1 meeting-icon-btn${value.isAfter(firstAvailableDay, "month") ? "" : " disabled"}`}
                        onClick={() => changeMonth(-1)}/>
                </Col>
                <Col className="meeting-calender-title">{selectedDate.format("MMM YYYY")}</Col>
                <Col>
                    <DoubleRightOutlined
                        className={`mr-1 meeting-icon-btn${value.isBefore(lastAvailableDay, "month") ? "" : " disabled"}`}
                        onClick={() => changeMonth(1)}/>
                </Col>
            </Row>
        );
    }

    const confirmForm = (justify) => {
        return (
            <Space direction="vertical" size="middle">
                <div key="confirm-time-info">
                    <Row type="flex" align="middle" justify={justify} gutter={[16, 4]}>
                        <Col>{`${getMessage(ClientMessages.ON, locale)} ${getSelectedTimeString()}`}</Col>
                        <Col><TimezoneDisplayName>{getSelectedTimezoneString()}</TimezoneDisplayName></Col>
                    </Row>
                </div>

                {(collectEmail || isFromPermanentLink) && <Form key="confirm-phone-form">
                    {isFromPermanentLink &&
                    <Form.Item className="mb-0" validateStatus={contactNameError ? "error" : ""} help={contactNameError}>
                        <Input value={contactName} placeholder={getMessage(ClientMessages.NAME, locale)} maxLength={40} autoFocus onChange={e => setContactName(e.target.value)}/>
                    </Form.Item>}

                    <Form.Item className="mb-0" validateStatus={contactEmailError ? "error" : ""} help={contactEmailError}>
                        <Input value={contactEmail} placeholder={getMessage(ClientMessages.EMAIL, locale)} maxLength={60} onChange={e => setContactEmail(e.target.value)}/>
                    </Form.Item>
                </Form>}

                {collectPhoneNumber && <Form key="confirm-phone-form">
                    <Row className="mb-25">
                        {getMessage(ClientMessages.NUMBER_PROMPT, locale, repName)}:
                    </Row>
                    <Form.Item className="mb-0" validateStatus={phoneNumberError ? "error" : ""} help={phoneNumberError}>
                        <Input value={phoneNumber} placeholder={getMessage(ClientMessages.PHONE_NUMBER, locale)} maxLength={20} autoFocus={!isFromPermanentLink}
                               onChange={e => setPhoneNumber(e.target.value)}/>
                    </Form.Item>
                </Form>}

            </Space>
        );
    }

    const hideConfirm = () => {
        setPhoneNumber(null);
        setSaving(false)
        setShowConfirm(false)
        setShowSlots(false);
    };

    const getMeetingLength = () => {
        return moment.duration(meetingLength, "milliseconds").asMinutes();
    }

    const onConfirm = async () => {
        if (collectPhoneNumber && (_.isEmpty(phoneNumber) || !testPhoneRegex(phoneNumber))) {
            setPhoneNumberError(getMessage(ClientMessages.VALID_PHONE, locale));
        } else if (isFromPermanentLink && _.isEmpty(contactName)) {
            setContactNameError(getMessage(ClientMessages.ENTER_NAME, locale));
        } else if ((collectEmail || isFromPermanentLink) && (_.isEmpty(contactEmail) || !testEmailRegex(contactEmail))) {
            setContactEmailError(getMessage(ClientMessages.VALID_EMAIL, locale));
        } else {
            setSaving(true);

            let success = false;

            try {
                ({success} = await setCustomMeeting(token, getSelectedTime(), timezone, phoneNumber, contactName, contactEmail));
            } catch (error) {
                success = false;
                Sentry.captureException(error, {user: contactEmail});
            }

            if (!success) {
                message.error(`${getMessage(ClientMessages.SET_MEETING_ERROR, locale)} :(`);
            }

            hideConfirm();
            setShowSuccess(success);
        }
    }

    if (loading) {
        return (
            <Row type="flex" justify="center" className="pt-2" gutter={[0, {xs: 16, lg: 48}]}>
                <Col xs={22} lg={18} xxl={16}>
                    <Skeleton paragraph={{rows: 6}} active loading/>
                </Col>
                <Col xs={22} lg={18} xxl={16}>
                    <Skeleton paragraph={{rows: 6}} active loading/>
                </Col>
            </Row>
        );
    }

    if (showSuccess) {
        return (<div className="meeting-notice-container flex-col-center">
            <Space direction="vertical" className="meeting-success-container p-5 pt-2">
                {botImgUrl && <ExceedAvatarComponent imageUrl={botImgUrl} size={42} shape={"square"} shortcut={repCompany?.charAt(0)}/>}
                <div className="mt-1 mb-2 text-success"><strong>✔️ {getMessage(ClientMessages.CONFIRMED, locale)}</strong></div>
                <div>
                    {getMessage(ClientMessages.MEETING_WITH, locale)}
                    <strong className="text-primary"> {repName} </strong>
                    {repCompany && <span>{getMessage(ClientMessages.FROM, locale)} <strong className="text-primary">{repCompany}</strong> </span>}
                    {getMessage(ClientMessages.SCHEDULED_FOR, locale)}:
                </div>
                <strong className="text-primary">{getSelectedTimeMoment().format("dddd, MMMM D")}</strong>
                <strong className="text-primary">{`${getSelectedTimeMoment().format("LT")} - ${getSelectedEndTimeMoment().format("LT")} (${getSelectedTimeMoment().format("zz")})`}</strong>
                <div>📅 {getMessage(ClientMessages.INVITE_SENT, locale)}</div>
                <div className="meeting-success-divider"><Divider /></div>
            </Space>
        </div>);
    }

    if (_.isEmpty(meetingSlots)) {
        return (
            <MeetingNotice icon={<CalendarOutlined className="meeting-icon info-icon"/>}>
                {repName && <div className="mb-5">{`${repName}${repCompany ? ` ${getMessage(ClientMessages.FROM, locale)} ${repCompany}` : ""}`}</div>}
                <div>{getMessage(ClientMessages.CALENDAR_UNAVAILABLE, locale)}</div>
            </MeetingNotice>
        );
    }

    return (
        <div className="flex-col meeting-notice-container parent">
            {/* eslint-disable-next-line */}
            {/*<a className="meeting-ribbon" target="_blank" href={`https://exceed.ai/?utm_source=scheduler${repCompany ? `&company=${repCompany}` : ""}`}>Powered by Exceed.ai</a>*/}
            <Row><Col xs={0}><br/><br/></Col></Row>
            <Row type="flex" justify="center" className="meeting-header-container">
                <Col xs={22} lg={16} xxl={12}>
                    <Row type="flex" justify="center" align="middle" gutter={16} className="meeting-header">
                        <Col flex="55px" className="text-right">
                            <ExceedAvatarComponent imageUrl={repImgUrl} shape={"square"} size={55} shortcut={repName.charAt(0)}/>
                        </Col>
                        <Col flex="1 1 400px" className="text-center meeting-header-text">
                            <span>
                                {getMessage(ClientMessages.MEETING_WITH, locale, getMeetingLength())}
                                <span style={{ whiteSpace: 'nowrap' }}>&nbsp;{repName}</span>
                                <span style={{ whiteSpace: 'nowrap' }}>&nbsp;{repCompany ? `${getMessage(ClientMessages.FROM, locale)} ${repCompany}` : ""}</span>
                            </span>
                        </Col>
                        <Col flex="55px">
                            <ExceedAvatarComponent imageUrl={botImgUrl} size={55} shape={"square"} shortcut={repCompany?.charAt(0)}/>
                        </Col>
                    </Row>
                    {scheduledMeeting && <Row type="flex" justify="center" className="meeting-subheader mt-25">
                        <Col className="text-center text-primary">
                            {`${getMessage(ClientMessages.CURRENTLY_SET, locale)} ${getScheduledTimeString()}`}
                        </Col>
                    </Row>}
                </Col>
            </Row>
            <Row type="flex" justify="center" align="top" className="meeting-overflow pb-1 flex-1">

                <Col xs={24} lg={18} xxl={13} className="parent">
                    <Row type="flex" justify="space-around" align="middle" className="meeting-full-height">

                        <Col xs={showingCalendar() ? 24 : 0} lg={12}>
                            <div className="meeting-calendar-container flex-t-c">
                                <Spin spinning={loadingMonth} wrapperClassName="meeting-spin-wrapper">
                                    <Calendar className="meeting-calendar" fullscreen={false} locale={{lang: {locale: moment.locale()}}}
                                              disabledDate={checkDate} headerRender={calendarHeader}
                                              monthCellRender={() => "Sahar"}
                                              value={selectedDate}
                                              onSelect={date => setSelectedDate(date)}/>
                                </Spin>
                            </div>
                        </Col>
                        <Col xs={showingSlots() ? 24 : 0} lg={8} className="meeting-slots-container">
                            <div className="pr-5 mb-1 flex-col-center">
                                <div className="meeting-subheader mb-5">{selectedDate.locale(locale).format("dddd, MMM Do")}</div>
                                <TimezoneDisplayName>{getTimezoneString()}</TimezoneDisplayName>
                            </div>
                            <PerfectScrollbar className="meeting-scrollbar" ref={scrollRef}>
                                <div className="flex-col-center meeting-slot-list">
                                    {getCurrentSlots().map(slot =>
                                        <div key={`slot_${slot.startTime.value}`}
                                             className="meeting-slot border-primary"
                                             onClick={() => onSlotClick(slot)}>
                                            {moment.tz(slot.startTime.value, timezone || slot.timeZoneName).format("LT")}
                                        </div>
                                    )}
                                </div>
                            </PerfectScrollbar>
                            <Row>
                                <Col xs={24} lg={0} className="mt-2 pl-2">
                                    <Button icon={<LeftOutlined/>} size="large" onClick={() => setShowSlots(false)}>{getMessage(ClientMessages.BACK, locale)}</Button>
                                </Col>
                            </Row>
                        </Col>
                        {showConfirm && <Col xs={24} lg={0} className="text-center meeting-conf-view">
                            {confirmForm("center")}
                            <Button type="primary" block size="large" className="mt-2" loading={saving} disabled={phoneNumberError ? true : false} onClick={onConfirm}>
                                {getMessage(ClientMessages.SET_MEETING, locale)}
                            </Button>
                            <Button block size="large" className="mt-1" onClick={hideConfirm}>
                                {getMessage(ClientMessages.CHOOSE_ANOTHER, locale)}
                            </Button>
                        </Col>}
                        {showCopyrights && <Col span={24}>
                            <div className={"rcw-copyrights"}>
                                <a href={"https://www.exceed.ai"} className={"rcw-copyrights-link"} target={"_blank"} rel="noopener noreferrer">
                                    <div>
                                        <span className={"rcw-copyrights-title"}>{getMessage(ClientMessages.POWERED_BY, locale)} Exceed</span>
                                        <span className={"rcw-copyrights-logo"}><img
                                            src={"https://s3.us-east-2.amazonaws.com/sdr.exceed.ai/logo_x_hires.png"}
                                            alt={"Exceed.ai"}/></span>
                                    </div>
                                </a>
                            </div>
                        </Col>}
                    </Row>
                </Col>
                <Col xs={0} lg={24}>
                    <Modal visible={showConfirm}
                           title={`${getMessage(ClientMessages.MEET, locale)} ${repName} ${repCompany ? `(${getMessage(ClientMessages.FROM, locale)} ${repCompany})` : ""}`}
                           getContainer={false} onCancel={hideConfirm}
                           footer={[
                               <Button key="cancel-btn" onClick={hideConfirm}>{getMessage(ClientMessages.CHOOSE_ANOTHER, locale)}</Button>,
                               <Button key="set-btn" className="meeting-save-btn" type="primary" loading={saving}
                                       autoFocus={!isFromPermanentLink && !collectPhoneNumber}
                                       disabled={phoneNumberError ? true : false} onClick={onConfirm}>
                                   {getMessage(ClientMessages.SET_MEETING, locale)}
                               </Button>
                           ]}>
                        {confirmForm("start")}
                    </Modal>
                </Col>
            </Row>
        </div>
    );
}

const MeetingNotice = (props) => {
    return (
        <div className="meeting-notice-container meeting-subheader flex-c-c">
            <div className="flex-middle meeting-notice-content">
                {props.icon}
                <div className="text-center">
                    {props.children}
                </div>
            </div>
        </div>
    );
}

const TimezoneDisplayName = (props) => {
    return (
        <div className="inline-flex-basline text-comment">
            <GlobalOutlined className="mr-25 text-medium"/>{props.children}
        </div>
    );
}

export default MeetingSchedulerPage;
